import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';

/**
 * A deploy replaces the app's hashed code files. A tab opened before it still runs the old code: its
 * lazily loaded parts (the watch window, the meeting, the interview room) are gone from the server, and
 * opening one used to leave a blank page. So: reload once when a part fails to load, show an error with
 * a reload button for anything else, and tell a recruiter on an old build that a new one is out.
 */

const RELOADED_AT = 'eduvy:reloaded-at';

const STALE_CODE =
  /dynamically imported module|importing a module script failed|failed to load module script|unable to preload|is not a valid javascript mime type/i;

export function isStaleCodeError(err: unknown): boolean {
  return STALE_CODE.test(err instanceof Error ? `${err.name} ${err.message}` : String(err));
}

/** Reload to pick up the new build, unless we just did (a real outage must not loop). */
export function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT) ?? 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(RELOADED_AT, String(Date.now()));
  } catch {
    /* private mode: reload anyway */
  }
  window.location.reload();
  return true;
}

/** Vite fires this when a lazily loaded part cannot be fetched. */
export function installStaleCodeReload(): void {
  window.addEventListener('vite:preloadError', (e) => {
    if (reloadOnce()) e.preventDefault();
  });
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  override state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    if (isStaleCodeError(error) && reloadOnce()) return;
    console.error('page crashed', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    const stale = isStaleCodeError(this.state.error);
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="font-display text-xl font-medium text-fg">
          {stale ? 'The dashboard was updated' : 'Something went wrong on this page'}
        </p>
        <p className="max-w-md text-sm text-muted-fg">
          {stale
            ? 'Reload to get the new version.'
            : 'Reload the page to carry on. If it keeps happening, tell us what you clicked.'}
        </p>
        <button
          className="rounded-sm bg-brand-gradient px-4 py-2 text-sm font-semibold text-white shadow-brand hover:brightness-110"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    );
  }
}

/** The file name of the build this tab is running (its entry script). */
function runningBuild(): string | null {
  const src = document
    .querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')
    ?.getAttribute('src');
  return src ? src.split('/').pop()! : null;
}

/** True once the server serves a different build from the one this tab runs. Checked every few minutes and on return to the tab. */
export function useNewVersion(intervalMs = 3 * 60_000): boolean {
  const [stale, setStale] = useState(false);
  useEffect(() => {
    const mine = runningBuild();
    if (!mine) return; // dev server: nothing to compare
    let stopped = false;
    const check = async () => {
      try {
        const html = await (
          await fetch('/', { cache: 'no-store', headers: { accept: 'text/html' } })
        ).text();
        if (!stopped && html.includes('/assets/') && !html.includes(mine)) setStale(true);
      } catch {
        /* offline: try again later */
      }
    };
    const timer = setInterval(check, intervalMs);
    const onVisible = () => document.visibilityState === 'visible' && void check();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs]);
  return stale;
}

export function NewVersionBanner() {
  const stale = useNewVersion();
  if (!stale) return null;
  return (
    <div
      className="flex flex-wrap items-center justify-center gap-3 bg-fg px-4 py-2 text-sm text-bg"
      role="status"
    >
      <span>The dashboard was updated. Reload to get the new version.</span>
      <button
        className="rounded-sm bg-bg px-3 py-1 text-xs font-semibold text-fg hover:opacity-90"
        onClick={() => window.location.reload()}
      >
        Reload
      </button>
    </div>
  );
}
